import { z } from 'zod';

/* ---------- Roles ---------- */

export const ROLES = [
  'Clinical Data Manager',
  'Medical Monitor',
  'CRA / Site Monitor',
  'Biostatistician',
  'Safety Physician',
  'Principal Investigator',
  'Sponsor Observer',
] as const;

export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

export interface RoleStyle {
  colourName: string;
  hex: string;
  short: string;
}

/** Role colours, bright enough to read on the charcoal design language. */
export const ROLE_STYLES: Record<Role, RoleStyle> = {
  'Clinical Data Manager': { colourName: 'teal', hex: '#2cc6c0', short: 'CDM' },
  'Medical Monitor': { colourName: 'navy', hex: '#5b8cff', short: 'MM' },
  'CRA / Site Monitor': { colourName: 'amber', hex: '#f2b44b', short: 'CRA' },
  Biostatistician: { colourName: 'violet', hex: '#a58bff', short: 'STAT' },
  'Safety Physician': { colourName: 'crimson', hex: '#ff6b81', short: 'SAF' },
  'Principal Investigator': { colourName: 'forest', hex: '#4fd18b', short: 'PI' },
  'Sponsor Observer': { colourName: 'grey', hex: '#94a0ae', short: 'OBS' },
};

/** The one and only role gate: Sponsor Observer can view but not raise, respond to or close. */
export function canModifyQueries(role: Role): boolean {
  return role !== 'Sponsor Observer';
}

/* ---------- Record references ---------- */

/** Every drillable record carries one of these; "Raise query" always receives one. */
export const RecordRefSchema = z.object({
  study: z.string().min(1),
  subjectId: z.string().min(1),
  visit: z.string().optional(),
  form: z.string().min(1),
  field: z.string().optional(),
  recordId: z.string().min(1),
});
export type RecordRef = z.infer<typeof RecordRefSchema>;

/* ---------- Queries ---------- */

export const QUERY_CATEGORIES = [
  'Data discrepancy',
  'Missing data',
  'Out of range',
  'Protocol question',
  'Safety follow-up',
  'Other',
] as const;
export const QueryCategorySchema = z.enum(QUERY_CATEGORIES);
export type QueryCategory = z.infer<typeof QueryCategorySchema>;

export const QUERY_PRIORITIES = ['Low', 'Normal', 'High'] as const;
export const QueryPrioritySchema = z.enum(QUERY_PRIORITIES);
export type QueryPriority = z.infer<typeof QueryPrioritySchema>;

export const QUERY_STATUSES = ['Open', 'Answered', 'Closed', 'Cancelled'] as const;
export const QueryStatusSchema = z.enum(QUERY_STATUSES);
export type QueryStatus = z.infer<typeof QueryStatusSchema>;

export const QUERY_SOURCES = ['seed', 'vr', 'web'] as const;
export const QuerySourceSchema = z.enum(QUERY_SOURCES);
export type QuerySource = z.infer<typeof QuerySourceSchema>;

/** Dashboards a query can be raised from; "PatientProfile" covers the ward and profile panel. */
export const DASHBOARD_IDS = [
  'Enrollment',
  'SubjectStatus',
  'VisitCompliance',
  'MissingData',
  'QueryManagement',
  'DataCleaning',
  'ProtocolDeviation',
  'LabAbnormality',
  'SAEReview',
  'RiskBasedMonitoring',
  'PatientProfile',
] as const;
export const DashboardIdSchema = z.enum(DASHBOARD_IDS);
export type DashboardId = z.infer<typeof DashboardIdSchema>;

export const DASHBOARD_TITLES: Record<DashboardId, string> = {
  Enrollment: 'Enrollment & Recruitment',
  SubjectStatus: 'Subject Status',
  VisitCompliance: 'Visit Compliance',
  MissingData: 'Missing Data',
  QueryManagement: 'Query Management',
  DataCleaning: 'Data Cleaning',
  ProtocolDeviation: 'Protocol Deviation',
  LabAbnormality: 'Lab Abnormality',
  SAEReview: 'SAE Review',
  RiskBasedMonitoring: 'Risk-Based Monitoring',
  PatientProfile: 'Patient Profiles',
};

/** Arc order, left to right, as hung in the study room. */
export const DASHBOARD_ARC_ORDER: readonly DashboardId[] = [
  'Enrollment',
  'SubjectStatus',
  'VisitCompliance',
  'MissingData',
  'QueryManagement',
  'DataCleaning',
  'ProtocolDeviation',
  'LabAbnormality',
  'SAEReview',
  'RiskBasedMonitoring',
  'PatientProfile',
];

export const IsoDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/, {
    message: 'Expected an ISO 8601 UTC timestamp',
  });

export const QueryActorSchema = z.object({
  name: z.string().min(1).max(64),
  role: RoleSchema,
  sessionId: z.string().min(1),
});
export type QueryActor = z.infer<typeof QueryActorSchema>;

export const QueryResponseSchema = z.object({
  by: z.string().min(1).max(64),
  role: RoleSchema,
  text: z.string().min(1).max(4000),
  at: IsoDateTimeSchema,
});
export type QueryResponse = z.infer<typeof QueryResponseSchema>;

export const PositionSchema = z.object({ x: z.number(), y: z.number(), z: z.number() });
export type Position = z.infer<typeof PositionSchema>;

export const QuerySchema = z.object({
  id: z.string().min(1),
  study: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visit: z.string().optional(),
  form: z.string().min(1),
  field: z.string().optional(),
  recordId: z.string().min(1),
  currentValue: z.string().optional(),
  dashboard: DashboardIdSchema,
  category: QueryCategorySchema,
  text: z.string().min(1).max(4000),
  priority: QueryPrioritySchema,
  status: QueryStatusSchema,
  raisedBy: QueryActorSchema,
  raisedAt: IsoDateTimeSchema,
  responses: z.array(QueryResponseSchema),
  closedBy: z.object({ name: z.string(), role: RoleSchema }).optional(),
  closedAt: IsoDateTimeSchema.optional(),
  position: PositionSchema.optional(),
  source: QuerySourceSchema,
});
export type Query = z.infer<typeof QuerySchema>;

/** Body of POST /api/queries. Server fills id, status, raisedAt, responses, source. */
export const CreateQueryInputSchema = z.object({
  study: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visit: z.string().optional(),
  form: z.string().min(1),
  field: z.string().optional(),
  recordId: z.string().min(1),
  currentValue: z.string().max(1000).optional(),
  dashboard: DashboardIdSchema,
  category: QueryCategorySchema,
  text: z.string().trim().min(3, 'Query text must be at least 3 characters').max(4000),
  priority: QueryPrioritySchema.default('Normal'),
  raisedBy: QueryActorSchema,
  position: PositionSchema.optional(),
  source: z.enum(['vr', 'web']).default('web'),
});
export type CreateQueryInput = z.input<typeof CreateQueryInputSchema>;

/** Body of POST /api/queries/:id/responses. */
export const AddResponseInputSchema = z.object({
  by: z.string().trim().min(1).max(64),
  role: RoleSchema,
  text: z.string().trim().min(1).max(4000),
});
export type AddResponseInput = z.input<typeof AddResponseInputSchema>;

/** Body of PATCH /api/queries/:id. */
export const UpdateQueryInputSchema = z.object({
  status: QueryStatusSchema,
  by: z.string().trim().min(1).max(64),
  role: RoleSchema,
});
export type UpdateQueryInput = z.input<typeof UpdateQueryInputSchema>;

/** Query string filters accepted by GET /api/queries and GET /api/queries.csv. */
export const QueryFilterSchema = z.object({
  study: z.string().optional(),
  status: z.string().optional(), // comma-separated statuses
  siteId: z.string().optional(),
  subjectId: z.string().optional(),
  category: z.string().optional(),
  priority: z.string().optional(),
  raisedBy: z.string().optional(),
  dashboard: z.string().optional(),
  source: z.string().optional(),
  from: z.string().optional(), // ISO date, inclusive
  to: z.string().optional(), // ISO date, inclusive
  q: z.string().optional(), // free text
  sort: z.string().optional(), // column name
  dir: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number().int().min(1).max(5000).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type QueryFilter = z.infer<typeof QueryFilterSchema>;

/** Which columns a client may sort by. Kept here so the server can whitelist. */
export const QUERY_SORT_COLUMNS = [
  'raisedAt',
  'study',
  'subjectId',
  'siteId',
  'visit',
  'form',
  'field',
  'category',
  'priority',
  'status',
  'raisedByName',
  'ageDays',
  'responseCount',
] as const;
export type QuerySortColumn = (typeof QUERY_SORT_COLUMNS)[number];

export interface QueryListResponse {
  total: number;
  items: Query[];
}

export interface QuerySummaryRow {
  study: string;
  open: number;
  answered: number;
  closed: number;
  cancelled: number;
}

export interface QuerySummary {
  perStudy: QuerySummaryRow[];
  /** Open query count at the end of each of the last 30 days, oldest first. */
  trend30d: { date: string; open: number; raised: number }[];
}

export function queryAgeDays(
  query: Pick<Query, 'raisedAt' | 'closedAt'>,
  now = Date.now(),
): number {
  const end = query.closedAt ? Date.parse(query.closedAt) : now;
  return Math.max(0, Math.floor((end - Date.parse(query.raisedAt)) / 86_400_000));
}
